import { Conditional, If, isAfter, isBefore, isSameTimeOrBefore, Row, stringToLocalDate } from "@hex-insights/core";
import { Icon } from "../../../../../Components/Icon";
import { Student, useStudentAcademicTermEnrollmentListQuery } from "../../../../../Utilities";
import { StudentAcademicTermEnrollmentFormat } from "../../../../Format";
import { AcademicTermUtilities } from "../../../../Utilities";

export type ControlledStudentEnrollmentStatusBadgeProps = {
	studentID: Student["id"];
};

export function ControlledStudentEnrollmentStatusBadge({ studentID }: ControlledStudentEnrollmentStatusBadgeProps) {
	const { loading: loadingAcademicTerm, selectedAcademicTerm } = AcademicTermUtilities.useAcademicTermsNavigation();
	const { loading, data } = useStudentAcademicTermEnrollmentListQuery({
		variables: {
			filters: { studentIDEQ: studentID },
		},
	});
	const enrollment =
		data?.studentAcademicTermEnrollmentConnection.edges.find((e) => e.node.academicTerm.id === selectedAcademicTerm?.id)
			?.node ?? null;

	return (
		<StudentEnrollmentStatusBadge
			isLoading={loading || loadingAcademicTerm}
			isEnrolled={!!enrollment}
			lateStartDate={enrollment?.lateStartDate}
			earlyEndDate={enrollment?.earlyEndDate}
		/>
	);
}

export type StudentEnrollmentStatusBadgeProps = {
	isLoading?: boolean;
	isEnrolled: boolean;
	lateStartDate?: string | null;
	earlyEndDate?: string | null;
};

export function StudentEnrollmentStatusBadge({
	isLoading = false,
	isEnrolled,
	lateStartDate,
	earlyEndDate,
}: StudentEnrollmentStatusBadgeProps) {
	const lateStartDateDate = lateStartDate ? stringToLocalDate(lateStartDate, "day") : null;
	const earlyEndDateDate = earlyEndDate ? stringToLocalDate(earlyEndDate, "day") : null;

	return (
		<Row
			justify="spaced-start"
			align="center"
			horizontalSpacing="0.375rem"
			style={{ color: "var(--theme-colors__teal)", fontWeight: "bold" }}
		>
			{isLoading || isEnrolled ? <Icon.CheckCircle size={16} /> : <Icon.XCircle size={16} />}
			<Conditional>
				<If condition={isLoading}>Loading...</If>
				<If condition={isEnrolled && !!lateStartDateDate && isBefore(new Date(), lateStartDateDate, "day")}>
					{lateStartDate && "Starts " + StudentAcademicTermEnrollmentFormat.Fields.lateStartDate(lateStartDate)}
				</If>
				<If condition={isEnrolled && !!earlyEndDateDate && isSameTimeOrBefore(new Date(), earlyEndDateDate, "day")}>
					{earlyEndDate && "Leaves " + StudentAcademicTermEnrollmentFormat.Fields.earlyEndDate(earlyEndDate)}
				</If>
				<If condition={isEnrolled && !!earlyEndDateDate && isAfter(new Date(), earlyEndDateDate, "day")}>
					{earlyEndDate && "Withdrawn since " + StudentAcademicTermEnrollmentFormat.Fields.earlyEndDate(earlyEndDate)}
				</If>
				<If condition={isEnrolled}>Enrolled</If>
				<If condition={!isEnrolled}>Not Enrolled</If>
			</Conditional>
		</Row>
	);
}
